import React, { Dispatch, FC, SetStateAction, useMemo } from 'react';
import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';

import { SearchIcon } from '../../components/icons/search';
import { EditIcon } from '../../components/icons/edit';
import { JournalConsultationFormatIcon } from '../../components/journal/journal-consultation-format-icon';
import { IClient, IConsultation } from '../../store/types';
import { declOfNum } from '../../utils/decOfNum';
import { getTime } from '../../utils/getTime';
import { SessionDetail, buildSessionDetailSession } from '../../components/journal/session-detail';
import { EmptyState, Heading, SearchInput, Pressable, TabList } from '../../shared/ui';

type Tab = {
  id: number;
  title: string;
};

interface JournalMobileProps {
  tabs: Tab[];
  activeTab: number;
  setActiveTab: (tabId: number) => void;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  clients: IClient[];
  consultations: IConsultation[];
  activeClient: number | null;
  setActiveClient: Dispatch<SetStateAction<number | null>>;
  onEditClient: (client: IClient) => void;
  pendingSessionDetail?: { consultationId: number; initialTab: string } | null;
  /** Консультации ещё грузятся — «сессия не найдена» показывать рано. */
  isSessionDetailPending?: boolean;
  onOpenSessionDetail: (consultationId: number, initialTab: string) => void;
  onCloseSessionDetail: () => void;
}

export const JournalMobile: FC<JournalMobileProps> = ({
  tabs,
  activeTab,
  setActiveTab,
  search,
  setSearch,
  clients,
  consultations,
  activeClient,
  setActiveClient,
  onEditClient,
  pendingSessionDetail = null,
  isSessionDetailPending = false,
  onOpenSessionDetail,
  onCloseSessionDetail,
}) => {
  // Открытая сессия — запись в истории браузера (проп), а не локальное состояние.
  const sessionDetail = pendingSessionDetail;
  const sortedClients = useMemo(() => {
    if (!clients?.length) {
      return [];
    }

    return [...clients].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru', { sensitivity: 'base' }));
  }, [clients]);

  const sortedConsultations = useMemo(() => {
    if (!consultations?.length) {
      return [];
    }

    return [...consultations].sort(
      (a, b) => new Date(b.datetime_start).getTime() - new Date(a.datetime_start).getTime(),
    );
  }, [consultations]);

  const consultationsLabel = `${sortedConsultations.length} ${declOfNum(sortedConsultations.length || 0, [
    'сессия',
    'сессии',
    'сессий',
  ])}`;

  const selectedTabTitle = tabs.find((tab) => tab.id === activeTab)?.title ?? 'Клиенты';
  const selectedClientName = sortedClients.find((client) => client.id === activeClient)?.full_name;
  const sessionDetailConsultation = useMemo(
    () => (sessionDetail ? sortedConsultations.find((item) => item.id === sessionDetail.consultationId) : undefined),
    [sessionDetail, sortedConsultations],
  );

  const handleClientSelect = (clientId: number) => {
    setActiveClient(clientId);
  };

  const handleOpenSession = (consultationId: number) => {
    onOpenSessionDetail(consultationId, 'Инструменты');
  };

  const renderClientChip = (client: IClient) => {
    const isActive = client.id === activeClient;
    return (
      <div
        key={client.id}
        className={classNames(
          'flex min-h-11 flex-shrink-0 items-stretch overflow-hidden rounded-full border text-ui-strong transition-colors duration-200',
          {
            'bg-primary border-primary text-white shadow-[0_8px_20px_rgba(199,0,0,0.35)]': isActive,
            'bg-white border-line text-ink': !isActive,
          },
        )}
      >
        <Pressable
          aria-pressed={isActive}
          onClick={() => handleClientSelect(client.id)}
          className="min-w-0 max-w-[190px] touch-manipulation truncate px-4 py-2 text-left"
        >
          {client.full_name}
        </Pressable>
        {isActive && (
          <Pressable
            aria-label={`${activeTab === 3 ? 'Редактировать встречу' : 'Редактировать клиента'} ${client.full_name}`}
            className="flex min-h-11 min-w-11 touch-manipulation items-center justify-center border-l border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20"
            onClick={() => onEditClient(client)}
          >
            <EditIcon width={18} height={18} stroke="currentColor" />
          </Pressable>
        )}
      </div>
    );
  };

  const renderConsultationRow = (consultation: IConsultation, index: number) => {
    const sessionDate = new Date(consultation.date);
    const sessionStart = new Date(consultation.datetime_start);
    const dateLabel = new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(sessionDate);
    const timeLabel = getTime(sessionStart);

    return (
      <Pressable
        key={consultation.id ?? index}
        onClick={() => handleOpenSession(consultation.id)}
        className="group flex min-h-[88px] w-full touch-manipulation items-center gap-3 rounded-card border border-line bg-white px-4 py-4 text-left [content-visibility:auto] transition-colors hover:border-primary active:bg-danger-surface"
        aria-label={`Открыть сессию ${consultation.consultation_number ?? index + 1} от ${dateLabel}, ${timeLabel}`}
      >
        <span className="w-5 shrink-0 text-body-l-strong leading-tight text-ink">
          {consultation.consultation_number ?? index + 1}
        </span>
        <JournalConsultationFormatIcon consultation={consultation} />
        <time dateTime={consultation.datetime_start} className="min-w-0 flex-1 text-body-l text-ink">
          <span className="block tabular-nums">{dateLabel}</span>
          <span className="block tabular-nums text-ui text-ink-subtle">{timeLabel}</span>
        </time>
        <span className="ml-auto inline-flex min-h-11 shrink-0 items-center gap-1 text-ui-strong text-ink-muted transition-colors group-hover:text-primary">
          <span className="hidden xs:inline">Открыть</span>
          <ChevronRight size={20} aria-hidden />
        </span>
      </Pressable>
    );
  };

  if (sessionDetail) {
    return (
      <div className="w-full min-w-0 overflow-x-hidden px-4 pb-[85px] pt-4">
        {sessionDetailConsultation ? (
          <SessionDetail
            consultationId={sessionDetail.consultationId}
            session={buildSessionDetailSession(sessionDetailConsultation)}
            initialTab={sessionDetail.initialTab}
            clientName={selectedClientName}
            onBack={onCloseSessionDetail}
          />
        ) : (
          <div className="rounded-card bg-white p-5 text-center text-ui text-ink-subtle">
            {isSessionDetailPending ? 'Загружаем сессию…' : 'Сессия не найдена в списке'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-hidden pb-[85px]">
      <div className="min-h-[420px] rounded-card bg-surface-muted p-4 shadow-raised-xl">
        <Heading size="xl" className="mb-5 text-pretty">
          Терапевтический журнал
        </Heading>

        <TabList
          variant="raised"
          className="mb-5"
          value={activeTab}
          onChange={setActiveTab}
          items={tabs.map((tab) => ({
            id: tab.id,
            label: tab.id === 1 ? 'Активные' : tab.id === 2 ? 'Архив' : 'Группы',
          }))}
        />

        <div className="mb-6">
          <SearchInput
            variant="large"
            name="journal-search"
            aria-label={activeTab === 3 ? 'Поиск встречи' : 'Поиск клиента'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={activeTab === 3 ? 'Поиск встречи…' : 'Поиск клиента…'}
            icon={<SearchIcon />}
          />
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <Heading size="sm" as="h2">
              {selectedTabTitle}
            </Heading>
            <span className="text-ui text-ink-subtle">{`${sortedClients.length} ${declOfNum(
              sortedClients.length || 0,
              activeTab === 3 ? ['встреча', 'встречи', 'встреч'] : ['клиент', 'клиента', 'клиентов'],
            )}`}</span>
          </div>
          {sortedClients.length ? (
            <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
              {sortedClients.map((client) => renderClientChip(client))}
            </div>
          ) : (
            <div className="text-ui text-ink-subtle">По вашему запросу ничего не найдено</div>
          )}
        </div>

        <main className="flex flex-col flex-grow">
          <div className="flex justify-between items-center mb-4">
            <div>
              <Heading size="sm" as="h2">
                {activeTab === 3 ? 'Журнал встречи' : 'Журнал клиента'}
              </Heading>
              {selectedClientName && <p className="text-ui text-ink-subtle mt-1">{selectedClientName}</p>}
            </div>
            <span className="text-ui text-ink-subtle">{consultationsLabel}</span>
          </div>

          {sortedConsultations.length > 0 ? (
            <div className="space-y-3">
              {sortedConsultations.map((consultation, index) => renderConsultationRow(consultation, index))}
            </div>
          ) : (
            <EmptyState>
              {activeClient
                ? activeTab === 3
                  ? 'Для выбранной встречи ещё нет записей в журнале'
                  : 'Для выбранного клиента ещё нет записей в журнале'
                : activeTab === 3
                  ? 'Выберите встречу, чтобы увидеть журнал'
                  : 'Выберите клиента, чтобы увидеть его журнал'}
            </EmptyState>
          )}
        </main>
      </div>
    </div>
  );
};

export default JournalMobile;
